import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

export const ALL = "__all__";

/** Paged search over 合同订单产品一览表. */
export function useOrderProductList({ enabled = true, defaultSearch = "" } = {}) {
  const [search, setSearch] = useState(defaultSearch);
  const [contractNo, setContractNo] = useState(ALL);
  const [customer, setCustomer] = useState(ALL);
  const [factory, setFactory] = useState(ALL);
  const [filters, setFilters] = useState({ contracts: [], customers: [], factories: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contract-orders", {
        params: {
          page: opts.page ?? page,
          page_size: pageSize,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(contractNo !== ALL ? { contract_no: contractNo } : {}),
          ...(customer !== ALL ? { customer } : {}),
          ...(factory !== ALL ? { factory } : {}),
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, contractNo, customer, factory]);

  useEffect(() => {
    if (!enabled) return;
    api.get("/contract-orders/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (enabled) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, pageSize]);

  const runSearch = () => { setPage(1); fetchRows({ page: 1 }); };
  const reset = () => {
    setSearch(""); setContractNo(ALL); setCustomer(ALL); setFactory(ALL); setPage(1);
    setTimeout(() => fetchRows({ page: 1 }), 0);
  };

  return {
    search, setSearch, contractNo, setContractNo, customer, setCustomer,
    factory, setFactory,
    filters, items, total, page, setPage, pageSize, setPageSize,
    loading, fetchRows, runSearch, reset,
    lastPage: Math.max(1, Math.ceil(total / pageSize)),
  };
}

/** 该产品历史最低采购价那一笔（含工厂/合同号/日期）。 */
export function useOrderProductLowest(itemNumber) {
  const [lowest, setLowest] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const key = (itemNumber || "").trim();
    if (!key) { setLowest(null); setCount(0); return; }
    let alive = true;
    api.get("/contract-orders/lowest", { params: { item_number: key } })
      .then(({ data }) => { if (alive) { setLowest(data.item); setCount(data.count || 0); } })
      .catch(() => { if (alive) { setLowest(null); setCount(0); } });
    return () => { alive = false; };
  }, [itemNumber]);

  return { lowest, count };
}

/** L1 exact → L2 normalised → L3 base-货号 match for one item number. */
export function useOrderProductMatch(itemNumber, limit = 20) {
  const [items, setItems] = useState([]);
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const key = (itemNumber || "").trim();
    if (!key) { setItems([]); setLevel(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/contract-orders/match", { params: { item_number: key, limit } });
      setItems(data.items || []);
      setLevel(data.level);
    } catch {
      setItems([]);
      setLevel(null);
    } finally {
      setLoading(false);
    }
  }, [itemNumber, limit]);

  useEffect(() => { load(); }, [load]);

  return { items, level, loading, reload: load };
}
